import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/feature/AppLayout';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

interface TicketEvent {
  id: string;
  ticket_id: string;
  event_type: string;
  actor: string;
  old_status: string | null;
  new_status: string | null;
  note: string | null;
  created_at: string;
}

interface Ticket {
  id: string;
  ticket_code: string;
  subject: string;
  message: string;
  status: 'open' | 'answered' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  admin_reply: string | null;
  user_comment: string | null;
  created_at: string;
  updated_at: string;
}

const statusColor: Record<string, string> = {
  open: 'bg-amber-50 text-amber-600 border-amber-200',
  answered: 'bg-green-50 text-green-600 border-green-200',
  closed: 'bg-gray-100 text-gray-500 border-gray-200',
};
const statusIcon: Record<string, string> = {
  open: 'ri-time-line',
  answered: 'ri-checkbox-circle-line',
  closed: 'ri-lock-line',
};
const priorityColor: Record<string, string> = {
  low: 'bg-gray-100 text-gray-500',
  normal: 'bg-sky-50 text-sky-600',
  high: 'bg-orange-50 text-orange-600',
  urgent: 'bg-red-50 text-red-600',
};

const PRIORITY_OPTIONS = ['all', 'low', 'normal', 'high', 'urgent'] as const;
const STATUS_OPTIONS = ['all', 'open', 'answered', 'closed'] as const;
const CATEGORY_OPTIONS = ['general', 'billing', 'technical', 'account', 'api', 'other'] as const;

function generateTicketCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const rand = () => chars[Math.floor(Math.random() * chars.length)];
  return `TKT-${rand()}${rand()}${rand()}${rand()}-${rand()}${rand()}${rand()}${rand()}`;
}

function ActivityTimeline({ ticketId }: { ticketId: string }) {
  const [events, setEvents] = useState<TicketEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    supabase
      .from('ticket_events')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        setEvents((data ?? []) as TicketEvent[]);
        setLoading(false);
      });
  }, [ticketId]);

  const eventMeta = (e: TicketEvent) => {
    if (e.new_status === 'open' && !e.old_status)
      return { icon: 'ri-add-circle-line', color: 'text-amber-500', bg: 'bg-amber-50', label: 'Ticket opened' };
    if (e.new_status === 'answered')
      return { icon: 'ri-checkbox-circle-line', color: 'text-green-600', bg: 'bg-green-50', label: 'Support replied' };
    if (e.new_status === 'closed')
      return { icon: 'ri-lock-line', color: 'text-gray-500', bg: 'bg-gray-100', label: 'Ticket closed' };
    if (e.event_type === 'reply')
      return { icon: 'ri-reply-line', color: 'text-brand-500', bg: 'bg-brand-50', label: `${e.actor} replied` };
    return { icon: 'ri-information-line', color: 'text-gray-400', bg: 'bg-gray-50', label: e.event_type };
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-gray-400">
        <i className="ri-loader-4-line animate-spin" /> Loading timeline…
      </div>
    );
  }
  if (events.length === 0) {
    return <p className="text-xs text-gray-400 py-3">No activity recorded yet.</p>;
  }

  return (
    <div className="relative pl-5">
      <div className="absolute left-[9px] top-3 bottom-3 w-px bg-gray-100" />
      <div className="space-y-4">
        {events.map((e) => {
          const { icon, color, bg, label } = eventMeta(e);
          return (
            <div key={e.id} className="relative flex gap-3 items-start">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 z-10 ${bg}`}>
                <i className={`${icon} text-[10px] ${color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-gray-800">{label}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">
                    {new Date(e.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <span className="text-[11px] text-gray-400">{e.actor}</span>
                {e.note && e.new_status !== 'open' && (
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{e.note}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SupportPage() {
  const { user } = useAuthContext();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [detailTab, setDetailTab] = useState<'conversation' | 'timeline'>('conversation');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normal', category: 'general' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<typeof STATUS_OPTIONS[number]>('all');
  const [priorityFilter, setPriorityFilter] = useState<typeof PRIORITY_OPTIONS[number]>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'updated'>('newest');
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentMsg, setCommentMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

  const fetchTickets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase.from('tickets').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
    setTickets((data ?? []) as Ticket[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const filtered = tickets
    .filter(t => {
      const q = search.toLowerCase();
      const matchSearch = !q || t.ticket_code.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q) || t.message.toLowerCase().includes(q) || (t.admin_reply ?? '').toLowerCase().includes(q);
      return matchSearch && (statusFilter === 'all' || t.status === statusFilter) && (priorityFilter === 'all' || t.priority === priorityFilter);
    })
    .sort((a, b) => {
      if (sortBy === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortBy === 'updated') return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const openTicket = (t: Ticket) => {
    setSelectedTicket(t);
    setCommentText(t.user_comment ?? '');
    setCommentMsg(null);
    setDetailTab('conversation');
  };

  const fireEmail = (ticketCode: string, status: string, userEmail: string, userName: string, subject: string, adminReply?: string) => {
    fetch(`${SUPABASE_URL}/functions/v1/ticket-status-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_code: ticketCode, status, user_email: userEmail, user_name: userName, subject, admin_reply: adminReply }),
    }).catch(() => {});
  };

  const submitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) { setMsg({ type: 'error', text: 'Subject and message are required.' }); return; }
    if (!user) return;
    setSubmitting(true);
    setMsg(null);
    const ticketCode = generateTicketCode();
    const { error } = await supabase.from('tickets').insert([{
      ticket_code: ticketCode, user_id: user.id, user_name: user.username, user_email: user.email,
      subject: form.subject.trim(), message: form.message.trim(), priority: form.priority, category: form.category, status: 'open',
    }]);
    setSubmitting(false);
    if (error) {
      setMsg({ type: 'error', text: error.message });
    } else {
      setMsg({ type: 'success', text: `Ticket ${ticketCode} submitted! Check your email for confirmation.` });
      setForm({ subject: '', message: '', priority: 'normal', category: 'general' });
      setShowForm(false);
      fetchTickets();
      fetch(`${SUPABASE_URL}/functions/v1/new-ticket-admin-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_code: ticketCode, user_email: user.email, user_name: user.username ?? user.email, subject: form.subject.trim(), message: form.message.trim(), priority: form.priority }),
      }).catch(() => {});
      fireEmail(ticketCode, 'open', user.email, user.username ?? user.email, form.subject.trim());
    }
    setTimeout(() => setMsg(null), 8000);
  };

  const saveComment = async () => {
    if (!selectedTicket || !commentText.trim()) return;
    setCommentSaving(true);
    setCommentMsg(null);
    const newStatus = selectedTicket.status === 'closed' ? 'open' : selectedTicket.status;
    const { error } = await supabase.from('tickets').update({ user_comment: commentText.trim(), status: newStatus, updated_at: new Date().toISOString() }).eq('id', selectedTicket.id);
    setCommentSaving(false);
    if (error) {
      setCommentMsg({ type: 'error', text: error.message });
    } else {
      const updated: Ticket = { ...selectedTicket, user_comment: commentText.trim(), status: newStatus };
      setSelectedTicket(updated);
      setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updated : t));
      setCommentMsg({ type: 'success', text: 'Your reply has been sent to the support team.' });
    }
    setTimeout(() => setCommentMsg(null), 5000);
  };

  const stats = { total: tickets.length, open: tickets.filter(t => t.status === 'open').length, answered: tickets.filter(t => t.status === 'answered').length, closed: tickets.filter(t => t.status === 'closed').length };
  const hasActiveFilters = !!(search || statusFilter !== 'all' || priorityFilter !== 'all' || sortBy !== 'newest');
  const clearFilters = () => { setSearch(''); setStatusFilter('all'); setPriorityFilter('all'); setSortBy('newest'); };

  return (
    <AppLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Support Center</h2>
            <p className="text-sm text-gray-400 mt-0.5">Submit and track your support tickets</p>
          </div>
          <button onClick={() => { setShowForm(true); setMsg(null); }} className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-add-line" />New Ticket
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Tickets', value: stats.total, icon: 'ri-customer-service-2-line', color: 'text-slate-600 bg-slate-100', filter: 'all' as const },
            { label: 'Open', value: stats.open, icon: 'ri-time-line', color: 'text-amber-500 bg-amber-50', filter: 'open' as const },
            { label: 'Answered', value: stats.answered, icon: 'ri-checkbox-circle-line', color: 'text-green-600 bg-green-50', filter: 'answered' as const },
            { label: 'Closed', value: stats.closed, icon: 'ri-lock-line', color: 'text-gray-500 bg-gray-100', filter: 'closed' as const },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              onClick={() => setStatusFilter(prev => prev === s.filter ? 'all' : s.filter)}
              className={`bg-white rounded-xl p-4 cursor-pointer transition-all ${statusFilter === s.filter && s.filter !== 'all' ? 'ring-2 ring-brand-400' : 'hover:ring-1 hover:ring-surface-3'}`}>
              <span className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${s.color}`}><i className={`${s.icon} text-base`} /></span>
              <div className="text-2xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {msg && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`flex items-start gap-3 px-4 py-3 rounded-xl text-sm border ${msg.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
              <i className={`${msg.type === 'success' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-error-warning-fill text-red-500'} shrink-0 mt-0.5`} />
              {msg.text}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-white rounded-2xl border border-surface-3 p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input type="text" placeholder="Search by ticket ID, subject or message…" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full text-sm pl-9 pr-9 py-2.5 border border-surface-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400" />
              {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"><i className="ri-close-line text-sm" /></button>}
            </div>
            <div className="flex items-center gap-1 bg-surface-2 rounded-xl p-1">
              {STATUS_OPTIONS.map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize cursor-pointer whitespace-nowrap transition-colors ${statusFilter === s ? 'bg-white text-gray-900 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}>{s}</button>
              ))}
            </div>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value as typeof PRIORITY_OPTIONS[number])}
              className="text-sm border border-surface-3 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer text-gray-600">
              <option value="all">All Priorities</option>
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as 'newest' | 'oldest' | 'updated')}
              className="text-sm border border-surface-3 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer text-gray-600">
              <option value="newest">Newest First</option><option value="oldest">Oldest First</option><option value="updated">Recently Updated</option>
            </select>
          </div>
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-surface-3 flex-wrap">
              <span className="text-xs text-gray-400">Filters:</span>
              {search && <span className="flex items-center gap-1 text-xs bg-brand-50 text-brand-600 px-2 py-1 rounded-full"><i className="ri-search-line text-[10px]" />&ldquo;{search}&rdquo;</span>}
              {statusFilter !== 'all' && <span className="flex items-center gap-1 text-xs bg-amber-50 text-amber-600 px-2 py-1 rounded-full capitalize"><i className="ri-flag-line text-[10px]" />Status: {statusFilter}</span>}
              {priorityFilter !== 'all' && <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-600 px-2 py-1 rounded-full capitalize"><i className="ri-arrow-up-line text-[10px]" />Priority: {priorityFilter}</span>}
              <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer ml-auto flex items-center gap-1"><i className="ri-close-circle-line" />Clear all</button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Your Tickets</h3>
              <span className="text-xs text-gray-400 bg-surface-2 px-2 py-0.5 rounded-full">{filtered.length}{filtered.length !== tickets.length ? ` of ${tickets.length}` : ''}</span>
            </div>
            <button onClick={fetchTickets} disabled={loading} className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1">
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
          </div>
          {loading ? (
            <div className="p-10 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-gray-300 block mb-2" /><p className="text-sm text-gray-400">Loading tickets…</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <i className={`text-4xl text-gray-200 block mb-3 ${hasActiveFilters ? 'ri-filter-off-line' : 'ri-customer-service-2-line'}`} />
              <p className="font-medium text-gray-500">{hasActiveFilters ? 'No tickets match your filters' : 'No tickets yet'}</p>
              <p className="text-sm text-gray-400 mt-1">
                {hasActiveFilters ? <button onClick={clearFilters} className="text-brand-500 hover:underline cursor-pointer">Clear filters</button> : 'Click "New Ticket" to submit your first support request'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-surface-3">
              {filtered.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="px-5 py-4 flex items-start gap-4 hover:bg-surface-2 cursor-pointer transition-colors group" onClick={() => openTicket(t)}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${t.status === 'open' ? 'bg-amber-50' : t.status === 'answered' ? 'bg-green-50' : 'bg-gray-100'}`}>
                    <i className={`${statusIcon[t.status]} text-sm ${t.status === 'open' ? 'text-amber-500' : t.status === 'answered' ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900 truncate">{t.subject}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${statusColor[t.status]}`}>{t.status}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${priorityColor[t.priority]}`}>{t.priority}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-[11px] font-mono text-gray-400">{t.ticket_code}</span>
                      <span className="text-[11px] text-gray-400"><i className="ri-calendar-line mr-0.5" />{new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      {t.user_comment && <span className="text-[11px] text-brand-500 flex items-center gap-1"><i className="ri-reply-line" />You replied</span>}
                      {t.admin_reply && !t.user_comment && <span className="text-[11px] text-green-600 flex items-center gap-1"><i className="ri-message-2-line" />Response received</span>}
                    </div>
                    {search && t.message.toLowerCase().includes(search.toLowerCase()) && (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-1 italic">&ldquo;…{t.message.substring(Math.max(0, t.message.toLowerCase().indexOf(search.toLowerCase()) - 20), t.message.toLowerCase().indexOf(search.toLowerCase()) + 60)}…&rdquo;</p>
                    )}
                    {!search && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{t.message}</p>}
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gray-400 shrink-0 mt-1 transition-colors" />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-surface-3">
                <div><h3 className="font-bold text-gray-900">Submit New Ticket</h3><p className="text-xs text-gray-400 mt-0.5">We typically respond within 24 hours</p></div>
                <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
              </div>
              <form onSubmit={submitTicket} className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block font-medium">Subject *</label>
                  <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Brief description of your issue"
                    value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} maxLength={200} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block font-medium">Category</label>
                    <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
                      value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                      <option value="general">General</option>
                      <option value="billing">Billing</option>
                      <option value="technical">Technical</option>
                      <option value="account">Account</option>
                      <option value="api">API</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block font-medium">Priority</label>
                    <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
                      value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                      <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block font-medium">Message *</label>
                  <textarea className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none" placeholder="Describe your issue in detail…"
                    rows={5} value={form.message} onChange={e => { if (e.target.value.length <= 500) setForm(f => ({ ...f, message: e.target.value })); }} maxLength={500} />
                  <div className="text-[11px] text-gray-400 text-right mt-0.5">{form.message.length}/500</div>
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-surface-3 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancel</button>
                  <button type="submit" disabled={submitting} className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    {submitting ? <><i className="ri-loader-4-line animate-spin" />Submitting…</> : <><i className="ri-send-plane-line" />Submit Ticket</>}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-surface-3 shrink-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900 truncate">{selectedTicket.subject}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${statusColor[selectedTicket.status]}`}>{selectedTicket.status}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${priorityColor[selectedTicket.priority]}`}>{selectedTicket.priority}</span>
                  </div>
                  <p className="text-[11px] font-mono text-gray-400 mt-0.5">{selectedTicket.ticket_code}</p>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer shrink-0 ml-3"><i className="ri-close-line text-gray-500" /></button>
              </div>

              {/* Tabs */}
              <div className="flex items-center border-b border-surface-3 px-6 shrink-0">
                {(['conversation', 'timeline'] as const).map(tab => (
                  <button key={tab} onClick={() => setDetailTab(tab)}
                    className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 -mb-px transition-colors cursor-pointer whitespace-nowrap ${detailTab === tab ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                    <i className={tab === 'conversation' ? 'ri-message-3-line' : 'ri-history-line'} />
                    {tab === 'conversation' ? 'Conversation' : 'Activity Timeline'}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {detailTab === 'timeline' && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Full Activity Log</h4>
                    <ActivityTimeline ticketId={selectedTicket.id} />
                  </div>
                )}

                {detailTab === 'conversation' && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-sidebar rounded-full flex items-center justify-center"><i className="ri-user-line text-white text-[10px]" /></div>
                          <span className="text-xs font-semibold text-gray-700">You</span>
                        </div>
                        <span className="text-[11px] text-gray-400">{new Date(selectedTicket.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedTicket.message}</p>
                    </div>

                    {selectedTicket.admin_reply ? (
                      <div className="bg-brand-50 border border-brand-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center"><i className="ri-customer-service-2-line text-white text-[10px]" /></div>
                          <span className="text-xs font-semibold text-brand-700">Support Team</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{selectedTicket.admin_reply}</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
                        <i className="ri-time-line text-amber-500 shrink-0" />Awaiting support team response — we typically reply within 24 hours.
                      </div>
                    )}

                    {selectedTicket.user_comment && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-sidebar rounded-full flex items-center justify-center"><i className="ri-user-line text-white text-[10px]" /></div>
                          <span className="text-xs font-semibold text-gray-700">Your Follow-up</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{selectedTicket.user_comment}</p>
                      </div>
                    )}

                    {selectedTicket.status !== 'closed' && (
                      <div className="border-t border-surface-3 pt-4 space-y-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
                            <i className="ri-reply-line text-brand-500 mr-1" />{selectedTicket.admin_reply ? 'Send a Follow-up Reply' : 'Add Additional Information'}
                          </label>
                          <textarea className="w-full text-sm border border-surface-3 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                            rows={4} placeholder={selectedTicket.admin_reply ? 'Write a follow-up message…' : 'Add any additional details…'}
                            value={commentText} onChange={e => { if (e.target.value.length <= 500) setCommentText(e.target.value); }} maxLength={500} />
                          <div className="text-[11px] text-gray-400 text-right mt-0.5">{commentText.length}/500</div>
                        </div>
                        <AnimatePresence>
                          {commentMsg && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs border ${commentMsg.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                              <i className={commentMsg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />{commentMsg.text}
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <button onClick={saveComment} disabled={commentSaving || !commentText.trim()}
                          className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                          {commentSaving ? <><i className="ri-loader-4-line animate-spin" />Sending…</> : <><i className="ri-send-plane-line" />Send Reply</>}
                        </button>
                      </div>
                    )}

                    {selectedTicket.status === 'closed' && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-500">
                        <i className="ri-lock-line shrink-0" />This ticket is closed. Open a new ticket if you need further assistance.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
